/**
 * API Services for Super Admin
 * ============================
 * خدمات API للمدير العام
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1';

// Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('watrly_admin_token');
  
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw { response: { data: error, status: response.status } };
  }

  return response.json();
};

// ═══════════════════════════════════════════════════════════
// Auth Service - خدمات المصادقة
// ═══════════════════════════════════════════════════════════
export const authService = {
  login: async (email, password) => {
    const response = await apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    // Map backend response to expected format
    return {
      access_token: response.access_token,
      user: response.user || { email, full_name: 'Admin' },
    };
  },

  logout: () => 
    apiCall('/auth/logout', { method: 'POST' }),

  getCurrentUser: () => 
    apiCall('/auth/me'),

  changePassword: (currentPassword, newPassword) =>
    apiCall('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }),
};

// ═══════════════════════════════════════════════════════════
// Tenants Service - خدمات المستأجرين
// ═══════════════════════════════════════════════════════════
export const tenantsService = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/tenants${query ? `?${query}` : ''}`);
  },

  getById: (id) => 
    apiCall(`/tenants/${id}`),

  create: (data) => 
    apiCall('/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id, data) => 
    apiCall(`/tenants/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id) => 
    apiCall(`/tenants/${id}`, { method: 'DELETE' }),

  suspend: (id) => 
    apiCall(`/tenants/${id}/suspend`, { method: 'POST' }),

  activate: (id) => 
    apiCall(`/tenants/${id}/activate`, { method: 'POST' }),

  getStats: (id) => 
    apiCall(`/tenants/${id}/stats`),

  getUsers: (id, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/tenants/${id}/users${query ? `?${query}` : ''}`);
  },
};

// ═══════════════════════════════════════════════════════════
// Users Service - خدمات المستخدمين
// ═══════════════════════════════════════════════════════════
export const usersService = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/users${query ? `?${query}` : ''}`);
  },

  getById: (id) => 
    apiCall(`/users/${id}`),

  create: (data) => 
    apiCall('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id, data) => 
    apiCall(`/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id) => 
    apiCall(`/users/${id}`, { method: 'DELETE' }),

  assignRole: (userId, roleId) =>
    apiCall(`/users/${userId}/roles`, {
      method: 'POST',
      body: JSON.stringify({ role_id: roleId }),
    }),

  removeRole: (userId, roleId) =>
    apiCall(`/users/${userId}/roles/${roleId}`, { method: 'DELETE' }),
};

// ═══════════════════════════════════════════════════════════
// Roles Service - خدمات الأدوار
// ═══════════════════════════════════════════════════════════
export const rolesService = {
  getAll: () => 
    apiCall('/roles'),

  getById: (id) => 
    apiCall(`/roles/${id}`),

  create: (data) => 
    apiCall('/roles', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id, data) => 
    apiCall(`/roles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id) => 
    apiCall(`/roles/${id}`, { method: 'DELETE' }),

  getPermissions: () => 
    apiCall('/permissions'),

  assignPermission: (roleId, permissionId) =>
    apiCall(`/roles/${roleId}/permissions`, {
      method: 'POST',
      body: JSON.stringify({ permission_id: permissionId }),
    }),
};

// ═══════════════════════════════════════════════════════════
// Dashboard Service - خدمات لوحة التحكم
// ═══════════════════════════════════════════════════════════
export const dashboardService = {
  getStats: () => 
    apiCall('/dashboard/stats'),

  getRecentTenants: (limit = 5) => 
    apiCall(`/dashboard/recent-tenants?limit=${limit}`),

  getRecentOrders: (limit = 5) => 
    apiCall(`/dashboard/recent-orders?limit=${limit}`),

  getRecentActivity: (limit = 10) => 
    apiCall(`/dashboard/activity?limit=${limit}`),

  getOrdersByStatus: () => 
    apiCall('/dashboard/orders-by-status'),

  getRevenueChart: (period = '7d') => 
    apiCall(`/dashboard/revenue-chart?period=${period}`),

  getDriversStatus: () => 
    apiCall('/dashboard/drivers-status'),
};

// ═══════════════════════════════════════════════════════════
// Drivers Service - خدمات السائقين
// ═══════════════════════════════════════════════════════════
export const driversService = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/drivers${query ? `?${query}` : ''}`);
  },

  getById: (id) => 
    apiCall(`/drivers/${id}`),

  getStats: () => 
    apiCall('/drivers/stats'),
};

// ═══════════════════════════════════════════════════════════
// Customers Service - خدمات العملاء
// ═══════════════════════════════════════════════════════════
export const customersService = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/customers${query ? `?${query}` : ''}`);
  },

  getById: (id) => 
    apiCall(`/customers/${id}`),

  getStats: () => 
    apiCall('/customers/stats'),
};

// ═══════════════════════════════════════════════════════════
// Orders Service - خدمات الطلبات
// ═══════════════════════════════════════════════════════════
export const ordersService = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/orders${query ? `?${query}` : ''}`);
  },

  getById: (id) => 
    apiCall(`/orders/${id}`),

  getStats: () => 
    apiCall('/orders/stats'),
};

// ═══════════════════════════════════════════════════════════
// Suppliers Service - خدمات الموردين
// ═══════════════════════════════════════════════════════════
export const suppliersService = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/suppliers${query ? `?${query}` : ''}`);
  },

  getById: (id) => 
    apiCall(`/suppliers/${id}`),

  getStats: () => 
    apiCall('/suppliers/stats'),
};

// ═══════════════════════════════════════════════════════════
// Ratings Service - خدمات التقييمات
// ═══════════════════════════════════════════════════════════
export const ratingsService = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/ratings${query ? `?${query}` : ''}`);
  },

  getById: (id) => 
    apiCall(`/ratings/${id}`),

  getStats: () => 
    apiCall('/ratings/stats'),

  create: (data) => 
    apiCall('/ratings', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateStatus: (id, status) => 
    apiCall(`/ratings/${id}/status?status=${status}`, {
      method: 'PATCH',
    }),

  delete: (id) => 
    apiCall(`/ratings/${id}`, { method: 'DELETE' }),
};

// ═══════════════════════════════════════════════════════════
// Chat Service - خدمات الدردشة
// ═══════════════════════════════════════════════════════════
export const chatService = {
  getSessions: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/chat/sessions${query ? `?${query}` : ''}`);
  },

  getSession: (id) => 
    apiCall(`/chat/sessions/${id}`),

  getStats: () => 
    apiCall('/chat/sessions/stats'),

  createSession: (data) => 
    apiCall('/chat/sessions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  sendMessage: (sessionId, data) => 
    apiCall(`/chat/sessions/${sessionId}/messages`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  closeSession: (id) => 
    apiCall(`/chat/sessions/${id}/close`, { method: 'PATCH' }),

  assignSession: (id, agentId) => 
    apiCall(`/chat/sessions/${id}/assign?agent_id=${agentId}`, { method: 'PATCH' }),

  deleteSession: (id) => 
    apiCall(`/chat/sessions/${id}`, { method: 'DELETE' }),
};

// ═══════════════════════════════════════════════════════════
// Tickets Service - خدمات التذاكر
// ═══════════════════════════════════════════════════════════
export const ticketsService = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/tickets${query ? `?${query}` : ''}`);
  },

  getById: (id) => 
    apiCall(`/tickets/${id}`),

  getStats: () => 
    apiCall('/tickets/stats'),

  create: (data) => 
    apiCall('/tickets', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateStatus: (id, status) => 
    apiCall(`/tickets/${id}/status?status=${status}`, {
      method: 'PATCH',
    }),

  assign: (id, assignedTo) => 
    apiCall(`/tickets/${id}/assign?assigned_to=${assignedTo}`, {
      method: 'PATCH',
    }),

  addMessage: (id, data) => 
    apiCall(`/tickets/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  delete: (id) => 
    apiCall(`/tickets/${id}`, { method: 'DELETE' }),
};

// ═══════════════════════════════════════════════════════════
// Registration Requests Service - خدمات طلبات التسجيل
// ═══════════════════════════════════════════════════════════
export const registrationRequestsService = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/registration-requests${query ? `?${query}` : ''}`);
  },

  getById: (id) => 
    apiCall(`/registration-requests/${id}`),

  getStats: () => 
    apiCall('/registration-requests/stats'),

  create: (data) => 
    apiCall('/registration-requests', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  approve: (id, notes = null) => 
    apiCall(`/registration-requests/${id}/approve${notes ? `?notes=${encodeURIComponent(notes)}` : ''}`, {
      method: 'POST',
    }),

  reject: (id, reason = null) => 
    apiCall(`/registration-requests/${id}/reject${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`, {
      method: 'POST',
    }),

  delete: (id) => 
    apiCall(`/registration-requests/${id}`, { method: 'DELETE' }),
};

// ═══════════════════════════════════════════════════════════
// Tenant Management Service - خدمات إدارة الشركات الشاملة
// ═══════════════════════════════════════════════════════════
export const tenantManagementService = {
  // Branches - الفروع
  getBranches: (tenantId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/tenant-management/${tenantId}/branches${query ? `?${query}` : ''}`);
  },
  createBranch: (tenantId, data) =>
    apiCall(`/tenant-management/${tenantId}/branches`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateBranch: (tenantId, branchId, data) =>
    apiCall(`/tenant-management/${tenantId}/branches/${branchId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // Ships - الأشياب
  getShips: (tenantId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/tenant-management/${tenantId}/ships${query ? `?${query}` : ''}`);
  },
  createShip: (tenantId, data) =>
    apiCall(`/tenant-management/${tenantId}/ships`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getShipDetails: (tenantId, shipId) =>
    apiCall(`/tenant-management/${tenantId}/ships/${shipId}`),
  updateShip: (tenantId, shipId, data) =>
    apiCall(`/tenant-management/${tenantId}/ships/${shipId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteShip: (tenantId, shipId) =>
    apiCall(`/tenant-management/${tenantId}/ships/${shipId}`, { method: 'DELETE' }),
  approveShip: (tenantId, shipId, notes = '') =>
    apiCall(`/tenant-management/${tenantId}/ships/${shipId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    }),
  rejectShip: (tenantId, shipId, reason = '') =>
    apiCall(`/tenant-management/${tenantId}/ships/${shipId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  getPendingShips: (params = {}) => {
    const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '' && v !== undefined));
    const query = new URLSearchParams(clean).toString();
    return apiCall(`/tenant-management/ships/pending${query ? `?${query}` : ''}`);
  },
  getAllShips: (params = {}) => {
    const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '' && v !== undefined));
    const query = new URLSearchParams(clean).toString();
    return apiCall(`/tenant-management/ships/all${query ? `?${query}` : ''}`);
  },

  // Drivers - السائقين
  getDrivers: (tenantId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/tenant-management/${tenantId}/drivers${query ? `?${query}` : ''}`);
  },
  getDriverDetails: (tenantId, driverId) =>
    apiCall(`/tenant-management/${tenantId}/drivers/${driverId}`),
  createDriver: (tenantId, data) =>
    apiCall(`/tenant-management/${tenantId}/drivers`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateDriver: (tenantId, driverId, data) =>
    apiCall(`/tenant-management/${tenantId}/drivers/${driverId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteDriver: (tenantId, driverId) =>
    apiCall(`/tenant-management/${tenantId}/drivers/${driverId}`, { method: 'DELETE' }),

  // Employees - الموظفين
  getEmployees: (tenantId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/tenant-management/${tenantId}/employees${query ? `?${query}` : ''}`);
  },
  createEmployee: (tenantId, data) =>
    apiCall(`/tenant-management/${tenantId}/employees`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Vehicles - المركبات
  getVehicles: (tenantId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/tenant-management/${tenantId}/vehicles${query ? `?${query}` : ''}`);
  },
  getVehicleDetails: (tenantId, vehicleId) =>
    apiCall(`/tenant-management/${tenantId}/vehicles/${vehicleId}`),
  createVehicle: (tenantId, data) =>
    apiCall(`/tenant-management/${tenantId}/vehicles`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateVehicle: (tenantId, vehicleId, data) =>
    apiCall(`/tenant-management/${tenantId}/vehicles/${vehicleId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteVehicle: (tenantId, vehicleId) =>
    apiCall(`/tenant-management/${tenantId}/vehicles/${vehicleId}`, { method: 'DELETE' }),

  // Pricing - التسعير
  getPricing: (tenantId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/tenant-management/${tenantId}/pricing${query ? `?${query}` : ''}`);
  },
  createPricing: (tenantId, data) =>
    apiCall(`/tenant-management/${tenantId}/pricing`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Coupons - الكوبونات
  getCoupons: (tenantId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/tenant-management/${tenantId}/coupons${query ? `?${query}` : ''}`);
  },

  // Promotions - العروض
  getPromotions: (tenantId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/tenant-management/${tenantId}/promotions${query ? `?${query}` : ''}`);
  },

  // Stats - الإحصائيات
  getStatsOverview: (tenantId, period = '30d') =>
    apiCall(`/tenant-management/${tenantId}/stats/overview?period=${period}`),
  getRevenueStats: (tenantId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/tenant-management/${tenantId}/stats/revenue${query ? `?${query}` : ''}`);
  },
  exportStats: (tenantId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/tenant-management/${tenantId}/stats/export${query ? `?${query}` : ''}`);
  },

  // Documents - الوثائق
  getDocuments: (tenantId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/tenant-management/${tenantId}/documents${query ? `?${query}` : ''}`);
  },

  // Awards - الجوائز
  getAwards: (tenantId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/tenant-management/${tenantId}/awards${query ? `?${query}` : ''}`);
  },
};

export default {
  auth: authService,
  tenants: tenantsService,
  users: usersService,
  roles: rolesService,
  dashboard: dashboardService,
  drivers: driversService,
  customers: customersService,
  orders: ordersService,
  suppliers: suppliersService,
  ratings: ratingsService,
  tickets: ticketsService,
  chat: chatService,
  registrationRequests: registrationRequestsService,
  tenantManagement: tenantManagementService,
};
